/**
 * English copy catalog for the `auth` feature (sign-in, registration, magic-link
 * verification, password reset / set). Keys map 1:1 to the `t('auth.*')` calls
 * in `packages/@gamecodex/world-ui/src/features/auth/*`. Values are the exact
 * original English strings.
 */
export const authEn = {
  // ── AuthShell ────────────────────────────────────────────────────────────
  'auth.shell.homeAriaLabel': 'Grymwyrd home',
  'auth.shell.logoAlt': 'Grymwyrd',

  // ── OAuthButtons ─────────────────────────────────────────────────────────
  'auth.oauth.continueWithGoogle': 'Continue with Google',
  'auth.oauth.continueWithDiscord': 'Continue with Discord',

  // ── AccountVerifyPage ────────────────────────────────────────────────────
  'auth.verify.seoTitle': 'Signing in',
  'auth.verify.seoDescription': 'Verifying your Grymwyrd sign-in link.',
  'auth.verify.errorMissingToken':
    'This sign-in link is missing its token. Request a new one from the sign-in page.',
  'auth.verify.errorInvalidOrExpired':
    'This sign-in link is invalid or has expired. Request a new one from the sign-in page.',
  'auth.verify.confirming': 'Confirming your sign-in…',
  'auth.verify.signedInHeading': "You're signed in",
  'auth.verify.signedInAsPrefix': 'Signed in as',
  'auth.verify.signedInAsSuffix': '. Taking you to your sanctum…',
  'auth.verify.takingYouToSanctum': 'Taking you to your sanctum…',
  'auth.verify.goNow': 'Go now',
  'auth.verify.problemHeading': 'Sign-in link problem',
  'auth.verify.backToSignIn': 'Back to sign-in',

  // ── LoginPage ────────────────────────────────────────────────────────────
  'auth.login.errorEnterEmailFirst': 'Enter your email above, then request a link.',
  'auth.login.seoTitle': 'Cross the Threshold',
  'auth.login.seoDescription': 'Sign in to Grymwyrd and return to your legend in the eternal twilight.',
  'auth.login.eyebrow': 'Return, Wanderer',
  'auth.login.title': 'Cross the Threshold',
  'auth.login.blurb': 'Speak your name and the secret word to re-enter the eternal twilight.',
  'auth.login.noPactYet': 'No pact yet?',
  'auth.login.forgeYourLegend': 'Forge your legend',
  'auth.login.emailLabel': 'Email',
  'auth.login.emailPlaceholder': 'you@grymwyrd.com',
  'auth.login.passwordLabel': 'Password',
  'auth.login.forgotPassword': 'Forgot your password?',
  'auth.login.submitBusy': 'Crossing…',
  'auth.login.submit': 'Enter',
  'auth.login.dividerOr': 'or',
  'auth.login.magicSentPrefix': 'A sign-in link is on its way',
  'auth.login.magicSentTo': 'to',
  'auth.login.magicSentSuffix': '. Check your inbox.',
  'auth.login.emailMeSignInLink': 'Email me a sign-in link',
  'auth.login.mockAuthPrefix': 'Dev mock auth: any email works. Use',
  'auth.login.mockAuthOr': 'or',
  'auth.login.mockAuthSuffix': 'for elevated roles.',

  // ── RegisterPage ─────────────────────────────────────────────────────────
  'auth.register.errorDisplayNameRequired': 'Choose the name your legend will be told under.',
  'auth.register.errorEmailRequired': 'An email is required to seal your pact.',
  'auth.register.errorEmailInvalid': 'That email does not look right.',
  'auth.register.errorPasswordTooShort': 'Your secret word must be at least 8 characters.',
  'auth.register.errorMustBeAdult': 'You must be 13 or older to join Grymwyrd.',
  'auth.register.errorMustAcceptTerms': 'You must accept the pact (terms) to continue.',
  'auth.register.errorIncomplete': 'Some marks on your pact are incomplete. See below.',
  'auth.register.seoTitle': 'Begin Your Tale',
  'auth.register.seoDescription':
    'Create a Grymwyrd account, raise an avatar, and let your story become canon.',
  'auth.register.eyebrow': 'Explore Greywick Village',
  'auth.register.title': 'Begin Your Tale',
  'auth.register.blurb': 'You wake from aetherial dreams and open your eyes…',
  'auth.register.alreadyBound': 'Already bound to the twilight?',
  'auth.register.loginToExisting': 'Login to Your Existing Account',
  'auth.register.displayNameLabel': 'Display name',
  'auth.register.displayNamePlaceholder': 'The Hollow Knight',
  'auth.register.displayNameHint': 'This names your public avatar and contributions.',
  'auth.register.emailLabel': 'Email',
  'auth.register.emailPlaceholder': 'you@grymwyrd.com',
  'auth.register.passwordLabel': 'Password',
  'auth.register.passwordPlaceholder': 'At least 8 characters',
  'auth.register.agreementsLegend': 'Agreements',
  'auth.register.adultConfirmPrefix': 'I confirm I am',
  'auth.register.adultConfirmEmphasis': '13 years of age or older',
  'auth.register.adultConfirmSuffix': '.',
  'auth.register.acceptPrefix': 'I accept the',
  'auth.register.termsOfThePact': 'Terms of the Pact',
  'auth.register.acceptSuffix': '.',
  'auth.register.submitBusy': 'Sealing the pact…',
  'auth.register.submit': 'Emerge',
  'auth.register.dividerOr': 'or',
  'auth.register.mockAuthNote': 'Dev mock auth: registration persists locally only. No email is sent.',
  'auth.register.lastUpdated': 'Last updated {date}',

  // ── ResetPasswordPage ────────────────────────────────────────────────────
  'auth.reset.errorInvalidEmail': 'Enter the email tied to your pact.',
  'auth.reset.seoTitle': 'Reset Your Password',
  'auth.reset.seoDescription':
    "Forgot your password? We'll email a one-time link; click it to choose a new password.",
  'auth.reset.eyebrow': 'Lost the Word',
  'auth.reset.title': 'Reset Your Password',
  'auth.reset.blurb':
    "Whisper your email and a one-time link will be sent into the dark. Follow it back, and you'll set a new password.",
  'auth.reset.rememberedAfterAll': 'Remembered it after all?',
  'auth.reset.loginToExisting': 'Login to Your Existing Account',
  'auth.reset.successMessage':
    'If an account exists for {email}, a one-time link has been sent. Open it to sign in and set your new password — check your email, and the spam crypt.',
  'auth.reset.sendToDifferentEmail': 'Send to a different email',
  'auth.reset.emailLabel': 'Email',
  'auth.reset.emailPlaceholder': 'you@grymwyrd.com',
  'auth.reset.submitBusy': 'Summoning…',
  'auth.reset.submit': 'Send Sign-In Link',

  // ── SetPasswordPage ──────────────────────────────────────────────────────
  'auth.setPassword.errorTooShort': 'Choose a password of at least 8 characters.',
  'auth.setPassword.errorMismatch': 'Those passwords do not match.',
  'auth.setPassword.seoTitle': 'Set Your Password',
  'auth.setPassword.seoDescription': 'Choose a new password for your Grymwyrd account.',
  'auth.setPassword.eyebrow': 'Seal the Pact',
  'auth.setPassword.title': 'Set a New Password',
  'auth.setPassword.blurb':
    "You're signed in. Choose a new password — you'll be able to use it next time alongside the magic link.",
  'auth.setPassword.ratherNot': 'Rather not?',
  'auth.setPassword.skipForNow': 'Skip for now',
  'auth.setPassword.successMessage': 'Your password is set. Taking you to your sanctum…',
  'auth.setPassword.newPasswordLabel': 'New password',
  'auth.setPassword.newPasswordPlaceholder': 'At least 8 characters',
  'auth.setPassword.confirmPasswordLabel': 'Confirm password',
  'auth.setPassword.confirmPasswordPlaceholder': 'Type it again',
  'auth.setPassword.submitBusy': 'Sealing…',
  'auth.setPassword.submit': 'Save Password',
} as const
